import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { summarizeDocumentSet, summarizeScoreGen } from '@features/scoreSpec/summary';
import { formatSpecId } from '@features/scoreSpec/format';
import { useFeedPrompt } from './useFeedPrompt';
import { EM_DASH } from './format';
import { scoreNameForFeed } from './specForFeed';

// What the model was actually asked, for the feed on screen.
//
// A score column is a number with no visible provenance; this is the question behind it. The
// document set comes along because a prompt read alone is the most likely thing to be misread —
// "score demand trends" against peers' transcripts and against the company's own filings are
// different questions, and the summaries reused here already state the mechanics that read
// backwards.
//
// The prompts are shown VERBATIM and labelled as templates. Rendering them would mean
// re-implementing the engine's Jinja render in the browser, which drifts from the real one; a
// prompt that looks rendered but is not is worse than an honest template.

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 16px;
`;

const PromptText = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.themeColors.text};
`;

const Label = styled.div`
  font-size: 10.5px;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  font-weight: 700;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

export const FeedPrompt = ({ feedId, canReadSpecs }) => {
  const { isAvailable, specId, isOpen, toggle, retry, isLoading, spec, error } = useFeedPrompt(feedId, {
    enabled: canReadSpecs,
  });

  if (!isAvailable) return null;

  const definition = (spec && spec.spec_json) || null;
  const scoreGen = (definition && definition.score_gen) || {};
  const scoreName = scoreNameForFeed(feedId);
  const panelId = 'feed-prompt-panel';

  return (
    <Container bottom={16}>
      <TransparentFlatButton onClick={toggle} buttonProps={{ 'aria-expanded': isOpen, 'aria-controls': panelId }}>
        {isOpen ? 'Hide the prompt' : 'Show the prompt'}
      </TransparentFlatButton>

      {isOpen && (
        <Container top={16}>
          <Panel id={panelId}>
            {isLoading && <Body5 isLowContrast>Loading the spec…</Body5>}

            {error && (
              <div role="alert">
                <Body5>{error}</Body5>
                <Container top={16}>
                  <TransparentFlatButton onClick={retry}>Try again</TransparentFlatButton>
                </Container>
              </div>
            )}

            {definition && (
              <div>
                <Body5 bold>{formatSpecId(specId)}</Body5>
                {definition.description && (
                  <Container top={4}>
                    <Body7 isLowContrast>{definition.description}</Body7>
                  </Container>
                )}
                {scoreName && (
                  <Container top={4}>
                    {/* A multi-score spec has ONE prompt, so sibling feeds show identical text and
                        differ only in which number they carry. Saying so stops that reading as a
                        bug. */}
                    <Body7 isLowContrast>
                      {`This feed carries the “${scoreName}” score. The other scores from this spec were produced by the same prompt.`}
                    </Body7>
                  </Container>
                )}

                <Container top={24}>
                  <Label>The question</Label>
                  <Container top={8}>
                    <PromptText>{scoreGen.prompt || EM_DASH}</PromptText>
                  </Container>
                </Container>

                {scoreGen.format_prompt && (
                  <Container top={24}>
                    <Label>The shape it was asked for</Label>
                    <Container top={8}>
                      <PromptText>{scoreGen.format_prompt}</PromptText>
                    </Container>
                  </Container>
                )}

                <Container top={24}>
                  <Label>What it read</Label>
                  <Container top={8}>
                    <Body7 isLowContrast>{summarizeDocumentSet(definition)}</Body7>
                  </Container>
                  <Container top={8}>
                    <Body7 isLowContrast>{summarizeScoreGen(definition)}</Body7>
                  </Container>
                </Container>

                <Container top={16}>
                  <Body7 isLowContrast>
                    Both prompts are Jinja2 templates, shown as authored — the engine substitutes the company and
                    document values at scoring time.
                  </Body7>
                </Container>
              </div>
            )}
          </Panel>
        </Container>
      )}
    </Container>
  );
};
